import type {
  CoreComposition,
  StellarAction,
  StellarActionResult,
  StellarContractId,
  StellarFate,
  StellarLegacyUnlockId,
  StellarMassId,
  StellarOfflineSummary,
  StellarStage,
  StellarState,
} from "../shared/stellar-types";

export const STELLAR_SCHEMA_VERSION = 2;
export const MAX_OFFLINE_SECONDS = 7 * 24 * 60 * 60;
const HISTORY_LIMIT = 40;
const MAX_PULSE_TOKENS = 40;
const PULSE_TOKENS_PER_SECOND = 5;
const STARTER_INSIGHT = 6;
const STARTER_STARLIGHT = 1.5;
const BASE_AUTOMATION_RATE = 1;

export interface StellarMassDefinition {
  id: StellarMassId;
  name: string;
  shortName: string;
  massSolar: number;
  mainTemperatureK: number;
  mainLuminositySolar: number;
  lifetimeYears: number;
  fate: StellarFate;
  remnantMassSolar: number;
  description: string;
  strategy: string;
}

export const STELLAR_MASSES: Record<StellarMassId, StellarMassDefinition> = {
  ember: {
    id: "ember",
    name: "余烬型低质量星",
    shortName: "0.8 M☉",
    massSolar: 0.8,
    mainTemperatureK: 5000,
    mainLuminositySolar: 0.4,
    lifetimeYears: 17_000_000_000,
    fate: "whiteDwarf",
    remnantMassSolar: 0.54,
    description: "较冷、较暗、燃料消耗慢，适合长期稳定观测。",
    strategy: "长寿命 · 低光度 · 白矮星",
  },
  sol: {
    id: "sol",
    name: "类太阳恒星",
    shortName: "1.0 M☉",
    massSolar: 1,
    mainTemperatureK: 5778,
    mainLuminositySolar: 1,
    lifetimeYears: 10_000_000_000,
    fate: "whiteDwarf",
    remnantMassSolar: 0.6,
    description: "平衡的入门路线，经历红巨星并留下白矮星。",
    strategy: "平衡 · 中等光度 · 白矮星",
  },
  titan: {
    id: "titan",
    name: "泰坦型高质量星",
    shortName: "12 M☉",
    massSolar: 12,
    mainTemperatureK: 25_000,
    mainLuminositySolar: 8_000,
    lifetimeYears: 18_000_000,
    fate: "coreCollapse",
    remnantMassSolar: 1.6,
    description: "极亮且演化迅速，可继续合成较重元素并发生核心坍缩。",
    strategy: "短寿命 · 高光度 · 中子星",
  },
};

export interface StellarStageDefinition {
  id: StellarStage;
  name: string;
  objective: string;
  threshold: number;
  science: string;
}

export const STELLAR_STAGES: Record<StellarStage, StellarStageDefinition> = {
  cloud: {
    id: "cloud",
    name: "分子云坍缩",
    objective: "集中冷气体，建立能继续坍缩的致密核心",
    threshold: 72,
    science: "引力使高密度区域继续收缩，坍缩释放的热量将形成原恒星。",
  },
  protostar: {
    id: "protostar",
    name: "原恒星点火",
    objective: "跟踪收缩升温，直到核心氢聚变稳定开始",
    threshold: 120,
    science: "原恒星首先由收缩供能；核心足够热和致密后，氢聚变开始。",
  },
  mainSequence: {
    id: "mainSequence",
    name: "主序氢聚变",
    objective: "维持观测节奏，记录核心氢转化为氦",
    threshold: 540,
    science: "核心氢聚变产生向外压力，与引力形成长期平衡。",
  },
  giant: {
    id: "giant",
    name: "巨星壳层燃烧",
    objective: "跟踪核心收缩、外层膨胀和更重元素形成",
    threshold: 390,
    science: "核心氢耗尽后平衡改变，外层膨胀；高质量星会继续经历更高级燃烧。",
  },
  terminal: {
    id: "terminal",
    name: "终末演化",
    objective: "闭合遗迹与抛射物的质量账本",
    threshold: 210,
    science: "低质量星抛去外层；高质量星的铁核心失去能量支撑并发生核心坍缩。",
  },
  remnant: {
    id: "remnant",
    name: "恒星遗迹",
    objective: "检查物质去向，并把富集材料带入下一代",
    threshold: 1,
    science: "遗迹保留致密核心，抛射物回到星际介质并可进入下一代恒星。",
  },
};

export interface StellarUpgradeDefinition {
  id: string;
  name: string;
  description: string;
  unlockStage: StellarStage;
  baseCost: number;
  maxLevel: number;
  currency: "insight" | "starlight" | "stardust";
  permanent?: boolean;
  effect: string;
}

export const STELLAR_UPGRADES: StellarUpgradeDefinition[] = [
  { id: "focusMirror", name: "引力聚焦镜", description: "把每次演化脉冲集中到更小的模型步长。", unlockStage: "cloud", baseCost: 8, maxLevel: 5, currency: "insight", effect: "脉冲推进 +0.7" },
  { id: "gravityClock", name: "引力时钟", description: "扩展无人观测链，让自动演化更快。", unlockStage: "cloud", baseCost: 2, maxLevel: 5, currency: "starlight", effect: "自动推进 +0.45/秒" },
  { id: "cloudSpectrometer", name: "云核光谱仪", description: "从每次脉冲中提取更多可用见解。", unlockStage: "cloud", baseCost: 28, maxLevel: 4, currency: "insight", effect: "见解获取 +20%" },
  { id: "angularMap", name: "角动量图谱", description: "减少原恒星模型中的无效旋转扰动。", unlockStage: "protostar", baseCost: 34, maxLevel: 4, currency: "insight", effect: "脉冲推进 +1.1" },
  { id: "convectionTracer", name: "对流追踪器", description: "用星光供能，自动跟踪原恒星内部能量输运。", unlockStage: "protostar", baseCost: 8, maxLevel: 4, currency: "starlight", effect: "自动推进 +0.8/秒" },
  { id: "ignitionForecast", name: "点火预报阵列", description: "在接近氢点火时减少观测盲区。", unlockStage: "protostar", baseCost: 40, maxLevel: 3, currency: "insight", effect: "阶段跃迁奖励 +20%" },
  { id: "photonCounter", name: "光子计数器", description: "收集更多可用于自动化仪器的星光。", unlockStage: "mainSequence", baseCost: 36, maxLevel: 5, currency: "insight", effect: "星光获取 +25%" },
  { id: "neutrinoScope", name: "中微子望远镜", description: "直接约束核心反应速率并增加见解。", unlockStage: "mainSequence", baseCost: 92, maxLevel: 4, currency: "insight", effect: "见解获取 +30%" },
  { id: "cadenceArray", name: "主序节拍阵列", description: "用星光维持长寿命主序观测的稳定自动节拍。", unlockStage: "mainSequence", baseCost: 30, maxLevel: 5, currency: "starlight", effect: "自动推进 +1.5/秒" },
  { id: "hrCalibrator", name: "H–R 标定器", description: "自动比对温度与光度，让每次观测产生更多见解。", unlockStage: "mainSequence", baseCost: 45, maxLevel: 3, currency: "insight", effect: "见解获取 +25%" },
  { id: "shellTomography", name: "壳层层析仪", description: "同时追踪核心和壳层燃烧。", unlockStage: "giant", baseCost: 165, maxLevel: 4, currency: "insight", effect: "脉冲推进 +2.2" },
  { id: "elementSpectrometer", name: "元素光谱阵列", description: "提高终局可回收的重元素记录质量。", unlockStage: "giant", baseCost: 190, maxLevel: 4, currency: "insight", effect: "星尘产量 +18%" },
  { id: "fusionRelay", name: "高级聚变中继", description: "用星光自动跟踪越来越短的高级燃烧阶段。", unlockStage: "giant", baseCost: 80, maxLevel: 4, currency: "starlight", effect: "自动推进 +2.8/秒" },
  { id: "shockTimer", name: "冲击波计时器", description: "提高终末阶段的时间分辨率。", unlockStage: "terminal", baseCost: 260, maxLevel: 3, currency: "insight", effect: "脉冲推进 +4" },
  { id: "ejectaLedger", name: "抛射物账本", description: "减少物质回收损失并核对质量守恒。", unlockStage: "terminal", baseCost: 300, maxLevel: 3, currency: "insight", effect: "星尘产量 +25%" },
  { id: "archiveMemory", name: "跨代档案记忆", description: "下一代保留更高效的脉冲模型。", unlockStage: "remnant", baseCost: 4, maxLevel: 6, currency: "stardust", permanent: true, effect: "永久脉冲推进 +0.5" },
  { id: "observerNetwork", name: "自治观测网络", description: "在基础自动观测上增加跨代并行阵列。", unlockStage: "remnant", baseCost: 6, maxLevel: 6, currency: "stardust", permanent: true, effect: "永久自动推进 +0.35/秒" },
  { id: "dustRecycling", name: "星尘循环协议", description: "提高后续每颗恒星的跨代星尘产量。", unlockStage: "remnant", baseCost: 8, maxLevel: 5, currency: "stardust", permanent: true, effect: "永久星尘产量 +15%" },
];

export interface StellarContractDefinition {
  id: StellarContractId;
  name: string;
  brief: string;
  preferredMass: StellarMassId;
  rewards: Record<StellarMassId, number>;
}

export const STELLAR_CONTRACTS: Record<StellarContractId, StellarContractDefinition> = {
  stableBeacon: {
    id: "stableBeacon",
    name: "长期稳定信标",
    brief: "优先选择寿命长、输出可持续的低质量恒星；快速演化不是本任务的优势。",
    preferredMass: "ember",
    rewards: { ember: 12, sol: 8, titan: 4 },
  },
  elementForge: {
    id: "elementForge",
    name: "快速元素回收",
    brief: "优先选择能经历高级燃烧并大量抛射物质的高质量恒星。",
    preferredMass: "titan",
    rewards: { ember: 2, sol: 5, titan: 12 },
  },
};

export interface StellarLegacyNode {
  id: StellarLegacyUnlockId;
  name: string;
  metalsRequired: number;
  effect: string;
}

export const STELLAR_LEGACY_NODES: StellarLegacyNode[] = [
  { id: "dustDiskArchive", name: "尘埃盘档案", metalsRequired: 2, effect: "永久见解获取 +10%" },
  { id: "metalLineArray", name: "金属线阵列", metalsRequired: 10, effect: "永久自动推进 +0.5/秒" },
  { id: "clusterNursery", name: "星团苗圃", metalsRequired: 25, effect: "永久星尘产量 +15%" },
];

const stageOrder: StellarStage[] = ["cloud", "protostar", "mainSequence", "giant", "terminal", "remnant"];

export class StellarRuleError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = "StellarRuleError";
  }
}

export function createInitialStellarState(nowIso = new Date().toISOString(), nowMs = Date.parse(nowIso)): StellarState {
  const tokenTime = Number.isFinite(nowMs) ? nowMs : Date.now();
  const state: StellarState = {
    schemaVersion: STELLAR_SCHEMA_VERSION,
    meta: {
      createdAt: nowIso,
      generation: 1,
      completedStars: 0,
      totalPulses: 0,
      totalSimulatedSeconds: 0,
      stardust: 0,
      recommendedNextMass: null,
      contractId: "stableBeacon",
      completedContracts: 0,
      ejectedMetals: 0,
      legacyUnlocks: [],
    },
    tutorial: {
      bootstrapComplete: false,
      firstPulseComplete: false,
      firstUpgradeComplete: false,
      automationIntroduced: false,
    },
    stage: "cloud",
    stageProgress: 0,
    star: null,
    resources: { insight: 0, starlight: 0 },
    upgrades: {},
    permanentUpgrades: {},
    discoveries: [],
    learning: {
      introducedConcepts: [],
      hrGuideComplete: false,
      fateScenarioId: null,
      massTransferScenarioId: null,
      hrIndependentScenarioId: null,
      hrTransferScenarioId: null,
      hintLevels: { "OBJ-MASS-FATE": 0, "OBJ-HR-EVIDENCE": 0 },
      attempts: [],
      mastered: { "OBJ-MASS-FATE": false, "OBJ-HR-EVIDENCE": false },
      transferComplete: { "OBJ-MASS-FATE": false, "OBJ-HR-EVIDENCE": false },
      lifetimeMastered: { "OBJ-MASS-FATE": false, "OBJ-HR-EVIDENCE": false },
      lifetimeTransferComplete: { "OBJ-MASS-FATE": false, "OBJ-HR-EVIDENCE": false },
      synthesisSeen: false,
    },
    history: [],
    integrity: { pulseTokens: MAX_PULSE_TOKENS, pulseTokenUpdatedAt: tokenTime },
    settings: { reducedMotion: false, compactNumbers: false },
  };
  initializeBootstrap(state);
  startStellarGeneration(state, "sol");
  addHistory(state, () => "initial-sol", "太阳级星云开始坍缩", "基础观测阵列正在自动运行。", "cloud");
  return state;
}

export function migrateStellarSave(value: unknown): StellarState {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("invalid stellar save");
  const candidate = structuredClone(value) as Partial<StellarState> & { schemaVersion?: number };
  if (candidate.schemaVersion === 1 && candidate.meta) {
    candidate.schemaVersion = STELLAR_SCHEMA_VERSION;
    candidate.meta.contractId = contractForGeneration(candidate.meta.generation ?? 1);
    candidate.meta.completedContracts = 0;
    const completedStars = Math.max(0, candidate.meta.completedStars ?? 0);
    const recoveredMetals = candidate.stage === "remnant" && candidate.star
      ? Math.max(0, completedStars - 1) + ejectedMetalsFor(candidate.star.massId)
      : completedStars;
    candidate.meta.ejectedMetals = recoveredMetals;
    candidate.meta.legacyUnlocks = STELLAR_LEGACY_NODES
      .filter((node) => recoveredMetals >= node.metalsRequired)
      .map((node) => node.id);
    if (candidate.resources && typeof candidate.resources.starlight !== "number") {
      candidate.resources.starlight = 0;
    }
    if (candidate.learning) {
      candidate.learning.lifetimeMastered = {
        "OBJ-MASS-FATE": Boolean(candidate.learning.mastered?.["OBJ-MASS-FATE"]),
        "OBJ-HR-EVIDENCE": Boolean(candidate.learning.mastered?.["OBJ-HR-EVIDENCE"]),
      };
      candidate.learning.lifetimeTransferComplete = {
        "OBJ-MASS-FATE": Boolean(candidate.learning.transferComplete?.["OBJ-MASS-FATE"]),
        "OBJ-HR-EVIDENCE": Boolean(candidate.learning.transferComplete?.["OBJ-HR-EVIDENCE"]),
      };
    }
  }
  if (candidate.schemaVersion !== STELLAR_SCHEMA_VERSION) throw new Error("unsupported stellar save schema");
  if (!candidate.meta || !candidate.tutorial || !candidate.learning || !candidate.integrity || !candidate.settings) {
    throw new Error("incomplete stellar save");
  }
  if (!stageOrder.includes(candidate.stage as StellarStage)) throw new Error("invalid stellar stage");
  if (candidate.star && !(candidate.star.massId in STELLAR_MASSES)) throw new Error("invalid stellar mass");
  const state = structuredClone(candidate) as StellarState;
  if (state.meta.generation === 1 && state.meta.completedStars === 0 && !state.star) {
    initializeBootstrap(state);
    startStellarGeneration(state, "sol");
    addHistory(state, () => "migration-sol", "太阳级星云开始坍缩", "基础观测阵列正在自动运行。", "cloud");
  } else if (state.star) {
    state.tutorial.bootstrapComplete = true;
    if (state.stage !== "remnant") state.tutorial.automationIntroduced = true;
  }
  assertFiniteState(state);
  return state;
}

export function stageThreshold(state: Pick<StellarState, "stage">): number {
  return STELLAR_STAGES[state.stage].threshold;
}

export function stageProgressRatio(state: Pick<StellarState, "stage" | "stageProgress">): number {
  return clamp(state.stageProgress / stageThreshold(state), 0, 1);
}

export function isStageReady(state: Pick<StellarState, "stage" | "stageProgress">): boolean {
  return state.stage !== "remnant" && state.stageProgress + 1e-9 >= stageThreshold(state);
}

export function pulsePower(state: StellarState): number {
  return round(1
    + level(state, "focusMirror") * 0.7
    + level(state, "angularMap") * 1.1
    + level(state, "shellTomography") * 2.2
    + level(state, "shockTimer") * 4
    + permanentLevel(state, "archiveMemory") * 0.5);
}

export function automationRate(state: StellarState): number {
  return round(
    (state.star && state.stage !== "remnant" ? BASE_AUTOMATION_RATE : 0)
    + level(state, "gravityClock") * 0.45
    + level(state, "convectionTracer") * 0.8
    + level(state, "cadenceArray") * 1.5
    + level(state, "fusionRelay") * 2.8
    + permanentLevel(state, "observerNetwork") * 0.35
    + (state.meta.legacyUnlocks.includes("metalLineArray") ? 0.5 : 0),
  );
}

export function insightMultiplier(state: StellarState): number {
  return 1
    + level(state, "cloudSpectrometer") * 0.2
    + level(state, "neutrinoScope") * 0.3
    + level(state, "hrCalibrator") * 0.25
    + (state.meta.legacyUnlocks.includes("dustDiskArchive") ? 0.1 : 0);
}

export function starlightMultiplier(state: StellarState): number {
  return 1 + level(state, "photonCounter") * 0.25;
}

export function upgradeCost(state: StellarState, definition: StellarUpgradeDefinition): number {
  const current = definition.permanent ? permanentLevel(state, definition.id) : level(state, definition.id);
  return Math.ceil(definition.baseCost * 1.72 ** current);
}

export function isUpgradeUnlocked(state: StellarState, definition: StellarUpgradeDefinition): boolean {
  if (!state.star) return false;
  return stageOrder.indexOf(state.stage) >= stageOrder.indexOf(definition.unlockStage);
}

export function simulateStellar(state: StellarState, elapsedRealSeconds: number): { state: StellarState; summary: StellarOfflineSummary } {
  const next = structuredClone(state);
  const elapsed = clamp(Number.isFinite(elapsedRealSeconds) ? elapsedRealSeconds : 0, 0, MAX_OFFLINE_SECONDS);
  const summary: StellarOfflineSummary = {
    elapsedRealSeconds: round(elapsed),
    simulatedSeconds: 0,
    progressGained: 0,
    insightGained: 0,
    starlightGained: 0,
    stageReady: isStageReady(next),
    messages: [],
  };
  if (elapsed <= 0 || !next.star || next.stage === "remnant" || isStageReady(next)) return { state: next, summary };
  const rate = automationRate(next);
  if (rate <= 0) return { state: next, summary };

  const remaining = Math.max(0, stageThreshold(next) - next.stageProgress);
  const progress = Math.min(remaining, rate * elapsed);
  const usedSeconds = progress / rate;
  const insight = progress * 0.12 * insightMultiplier(next);
  const starlight = starlightForProgress(next, progress);
  next.stageProgress = round(next.stageProgress + progress);
  next.resources.insight = round(next.resources.insight + insight);
  next.resources.starlight = round(next.resources.starlight + starlight);
  next.meta.totalSimulatedSeconds = round(next.meta.totalSimulatedSeconds + usedSeconds);
  updateStellarPhysics(next);
  summary.simulatedSeconds = round(usedSeconds);
  summary.progressGained = round(progress);
  summary.insightGained = round(insight);
  summary.starlightGained = round(starlight);
  summary.stageReady = isStageReady(next);
  if (summary.stageReady) summary.messages.push(`${STELLAR_STAGES[next.stage].name}的进度已满，等待你确认阶段跃迁。`);
  assertFiniteState(next);
  return { state: next, summary };
}

export function applyStellarAction(
  state: StellarState,
  action: StellarAction,
  options: { nowMs?: number; id?: () => string } = {},
): StellarActionResult {
  const next = structuredClone(state);
  const messages: string[] = [];
  const nowMs = options.nowMs ?? Date.now();
  const id = options.id ?? (() => crypto.randomUUID());

  switch (action.type) {
    case "COMPLETE_BOOTSTRAP": {
      if (!next.tutorial.bootstrapComplete) {
        initializeBootstrap(next);
        addHistory(next, id, "基础观测阵列上线", "质量标尺已经标定。", "cloud");
        messages.push("基础观测阵列已上线。 ");
      }
      break;
    }
    case "SELECT_STAR": {
      if (next.star) throw new StellarRuleError("STAR_ALREADY_SELECTED", "当前一代已经选定恒星");
      initializeBootstrap(next);
      const definition = STELLAR_MASSES[action.massId];
      startStellarGeneration(next, action.massId);
      addHistory(next, id, "选择恒星胚胎", `${definition.shortName} · ${definition.strategy}`, "cloud");
      messages.push(`${definition.name}已开始自动演化。`);
      break;
    }
    case "PULSE": {
      requireActiveStar(next);
      if (!Number.isInteger(action.count) || action.count < 1 || action.count > 25) {
        throw new StellarRuleError("INVALID_PULSE_COUNT", "单批演化脉冲必须在 1 到 25 次之间");
      }
      if (isStageReady(next)) throw new StellarRuleError("STAGE_READY", "请先确认当前阶段的演化结果");
      const remainingProgress = stageThreshold(next) - next.stageProgress;
      const effectiveCount = Math.min(action.count, Math.ceil(remainingProgress / pulsePower(next)));
      consumePulseTokens(next, effectiveCount, nowMs);
      const progress = Math.min(remainingProgress, pulsePower(next) * effectiveCount);
      const insight = effectiveCount * insightMultiplier(next);
      const starlight = starlightForProgress(next, progress);
      next.stageProgress = round(next.stageProgress + progress);
      next.resources.insight = round(next.resources.insight + insight);
      next.resources.starlight = round(next.resources.starlight + starlight);
      next.meta.totalPulses += effectiveCount;
      next.tutorial.firstPulseComplete = true;
      updateStellarPhysics(next);
      if (isStageReady(next)) messages.push(`${STELLAR_STAGES[next.stage].name}已达到跃迁条件。`);
      break;
    }
    case "BUY_UPGRADE": {
      requireActiveStar(next, true);
      const definition = STELLAR_UPGRADES.find((item) => item.id === action.upgradeId);
      if (!definition) throw new StellarRuleError("UPGRADE_NOT_FOUND", "未找到该升级");
      if (!isUpgradeUnlocked(next, definition)) throw new StellarRuleError("UPGRADE_LOCKED", "该仪器尚未解锁");
      const current = definition.permanent ? permanentLevel(next, definition.id) : level(next, definition.id);
      if (current >= definition.maxLevel) throw new StellarRuleError("UPGRADE_MAXED", "该升级已达到最高等级");
      const cost = upgradeCost(next, definition);
      if (definition.currency === "stardust") {
        if (next.meta.stardust + 1e-9 < cost) throw new StellarRuleError("NOT_ENOUGH_STARDUST", "星尘记忆不足");
        next.meta.stardust = round(next.meta.stardust - cost);
        next.permanentUpgrades[definition.id] = current + 1;
      } else {
        if (next.resources[definition.currency] + 1e-9 < cost) {
          throw new StellarRuleError(
            definition.currency === "starlight" ? "NOT_ENOUGH_STARLIGHT" : "NOT_ENOUGH_INSIGHT",
            definition.currency === "starlight" ? "星光不足" : "见解不足",
          );
        }
        next.resources[definition.currency] = round(next.resources[definition.currency] - cost);
        next.upgrades[definition.id] = current + 1;
      }
      next.tutorial.firstUpgradeComplete = true;
      if (automationRate(next) > 0) next.tutorial.automationIntroduced = true;
      addHistory(next, id, `升级：${definition.name}`, `${definition.effect} · 等级 ${current + 1}`, next.stage);
      messages.push(`${definition.name}已提升到 ${current + 1} 级。`);
      break;
    }
    case "ADVANCE_STAGE": {
      requireActiveStar(next);
      if (!isStageReady(next)) throw new StellarRuleError("STAGE_NOT_READY", "当前阶段进度尚未完成");
      const currentIndex = stageOrder.indexOf(next.stage);
      const target = stageOrder[currentIndex + 1];
      if (!target) throw new StellarRuleError("NO_NEXT_STAGE", "已经抵达恒星遗迹");
      const completedStage = next.stage;
      next.stage = target;
      next.stageProgress = target === "remnant" ? 1 : 0;
      const reward = stageReward(completedStage) * (1 + level(next, "ignitionForecast") * 0.2);
      next.resources.insight = round(next.resources.insight + reward);
      updateStellarPhysics(next);
      const discovery = discoveryForStage(target, next.star!);
      if (!next.discoveries.includes(discovery)) next.discoveries.push(discovery);
      addHistory(next, id, `进入${stageDisplayName(next)}`, STELLAR_STAGES[target].science, target);
      messages.push(`阶段跃迁：${stageDisplayName(next)}。`);
      if (target === "mainSequence") messages.push("H–R 图已上线，可持续观察温度与光度的变化。 ");
      if (target === "giant") messages.push("恒星外层膨胀；自治观测继续追踪壳层燃烧。 ");
      if (target === "remnant") {
        const dust = stellarDustYield(next);
        next.meta.stardust = round(next.meta.stardust + dust);
        next.meta.completedStars += 1;
        const metals = ejectedMetalsFor(next.star!.massId);
        next.meta.ejectedMetals = round(next.meta.ejectedMetals + metals);
        if (next.star!.massId === STELLAR_CONTRACTS[next.meta.contractId].preferredMass) {
          next.meta.completedContracts += 1;
        }
        const unlocked = unlockLegacyNodes(next);
        messages.push(`物质账本闭合，获得 ${dust} 星尘记忆与 ${metals} 份可追踪抛射金属。`);
        if (unlocked.length) messages.push(`跨代材料解锁：${unlocked.map((item) => item.name).join("、")}。`);
      }
      break;
    }
    case "PRESTIGE": {
      if (next.stage !== "remnant" || !next.star) throw new StellarRuleError("PRESTIGE_LOCKED", "必须先完成当前恒星");
      next.meta.generation += 1;
      next.meta.contractId = contractForGeneration(next.meta.generation);
      next.stage = "cloud";
      next.stageProgress = 0;
      next.star = null;
      next.resources = { insight: 0, starlight: 0 };
      next.upgrades = {};
      next.tutorial.firstPulseComplete = false;
      next.tutorial.firstUpgradeComplete = false;
      next.tutorial.automationIntroduced = permanentLevel(next, "observerNetwork") > 0;
      next.learning.hrGuideComplete = false;
      next.learning.fateScenarioId = null;
      next.learning.hrIndependentScenarioId = null;
      next.learning.hrTransferScenarioId = null;
      next.learning.massTransferScenarioId = null;
      next.learning.hintLevels = { "OBJ-MASS-FATE": 0, "OBJ-HR-EVIDENCE": 0 };
      next.learning.mastered = { "OBJ-MASS-FATE": false, "OBJ-HR-EVIDENCE": false };
      next.learning.transferComplete = { "OBJ-MASS-FATE": false, "OBJ-HR-EVIDENCE": false };
      next.learning.synthesisSeen = false;
      addHistory(next, id, `第 ${next.meta.generation} 代星云已建立`, "上一代抛射物与观测档案已经写入星尘记忆。", "cloud");
      messages.push(`第 ${next.meta.generation} 代已开始。请选择新的质量路线。`);
      break;
    }
    case "SYNC":
      break;
    case "CHANGE_STELLAR_SETTINGS": {
      if (action.settings.reducedMotion !== undefined) next.settings.reducedMotion = action.settings.reducedMotion;
      if (action.settings.compactNumbers !== undefined) next.settings.compactNumbers = action.settings.compactNumbers;
      messages.push("星核界面设置已保存。 ");
      break;
    }
  }

  assertFiniteState(next);
  return { state: next, messages };
}

export function stellarDustYield(state: StellarState): number {
  if (!state.star) return 0;
  const contract = STELLAR_CONTRACTS[state.meta.contractId];
  const base = contract.rewards[state.star.massId];
  const contractMatch = state.star.massId === contract.preferredMass ? 1.2 : 1;
  const instrument = 1 + level(state, "elementSpectrometer") * 0.18 + level(state, "ejectaLedger") * 0.25;
  const permanent = 1
    + permanentLevel(state, "dustRecycling") * 0.15
    + (state.meta.legacyUnlocks.includes("clusterNursery") ? 0.15 : 0);
  return Math.max(1, Math.round(base * contractMatch * instrument * permanent));
}

export function hrPosition(temperatureK: number, luminositySolar: number): { x: number; y: number } {
  const minTemperature = Math.log10(3_000);
  const maxTemperature = Math.log10(30_000);
  const temperature = clamp(Math.log10(Math.max(temperatureK, 1)), minTemperature, maxTemperature);
  const luminosity = clamp(Math.log10(Math.max(luminositySolar, 1e-4)), -3, 5);
  return {
    x: round(8 + (maxTemperature - temperature) / (maxTemperature - minTemperature) * 84),
    y: round(92 - (luminosity + 3) / 8 * 84),
  };
}

function initializeBootstrap(state: StellarState): void {
  state.tutorial.bootstrapComplete = true;
}

function startStellarGeneration(state: StellarState, massId: StellarMassId): void {
  const definition = STELLAR_MASSES[massId];
  state.stage = "cloud";
  state.stageProgress = 0;
  state.star = createStar(definition);
  state.upgrades = {};
  state.resources = { insight: STARTER_INSIGHT, starlight: STARTER_STARLIGHT };
  state.tutorial.firstPulseComplete = false;
  state.tutorial.firstUpgradeComplete = false;
  state.tutorial.automationIntroduced = true;
  state.learning.hrGuideComplete = false;
  state.learning.fateScenarioId = null;
  state.learning.massTransferScenarioId = null;
  state.learning.hrIndependentScenarioId = null;
  state.learning.hrTransferScenarioId = null;
  state.learning.hintLevels = { "OBJ-MASS-FATE": 0, "OBJ-HR-EVIDENCE": 0 };
  updateStellarPhysics(state);
}

function createStar(definition: StellarMassDefinition) {
  return {
    massId: definition.id,
    massSolar: definition.massSolar,
    surfaceTemperatureK: 15,
    luminositySolar: 0,
    radiusSolar: 0.02,
    lifetimeYears: definition.lifetimeYears,
    ageFraction: 0,
    fate: definition.fate,
    remnantMassSolar: definition.remnantMassSolar,
    ejectaMassSolar: round(definition.massSolar - definition.remnantMassSolar),
    composition: normalizeComposition({ hydrogen: 0.7, helium: 0.28, carbonOxygen: 0.018, silicon: 0.0015, iron: 0.0005 }),
  };
}

function updateStellarPhysics(state: StellarState): void {
  const star = state.star;
  if (!star) return;
  const mass = STELLAR_MASSES[star.massId];
  const progress = stageProgressRatio(state);
  const index = stageOrder.indexOf(state.stage);
  star.ageFraction = round(clamp((index + progress) / (stageOrder.length - 1), 0, 1));

  switch (state.stage) {
    case "cloud":
      star.surfaceTemperatureK = Math.round(15 + 55 * progress);
      star.luminositySolar = round(0.0001 + 0.002 * progress);
      star.radiusSolar = round(0.02 + 0.18 * progress);
      star.composition = normalizeComposition({ hydrogen: 0.7, helium: 0.28, carbonOxygen: 0.018, silicon: 0.0015, iron: 0.0005 });
      break;
    case "protostar":
      star.surfaceTemperatureK = Math.round(2_800 + (mass.mainTemperatureK * 0.82 - 2_800) * progress);
      star.luminositySolar = round(Math.max(0.05, mass.mainLuminositySolar * (1.8 - 0.8 * progress)));
      star.radiusSolar = round(3.2 - 2.2 * progress);
      star.composition = normalizeComposition({ hydrogen: 0.7, helium: 0.28, carbonOxygen: 0.018, silicon: 0.0015, iron: 0.0005 });
      break;
    case "mainSequence": {
      star.surfaceTemperatureK = Math.round(mass.mainTemperatureK * (1 + 0.04 * progress));
      star.luminositySolar = round(mass.mainLuminositySolar * (1 + 0.28 * progress));
      star.radiusSolar = round((star.massId === "titan" ? 5.2 : star.massId === "ember" ? 0.75 : 1) * (1 + 0.14 * progress));
      const burned = 0.68 * progress;
      star.composition = normalizeComposition({ hydrogen: 0.7 - burned, helium: 0.28 + burned, carbonOxygen: 0.018, silicon: 0.0015, iron: 0.0005 });
      break;
    }
    case "giant": {
      const giantLuminosity = star.massId === "titan" ? 45_000 : star.massId === "ember" ? 420 : 900;
      const giantTemperature = star.massId === "titan" ? 4_400 : 3_700;
      const giantEntryTemperature = star.massId === "titan" ? 6_000 : star.massId === "ember" ? 4_100 : 4_500;
      const giantEntryRadius = star.massId === "titan" ? 100 : 25;
      star.surfaceTemperatureK = Math.round(giantEntryTemperature + (giantTemperature - giantEntryTemperature) * progress);
      star.luminositySolar = round(giantLuminosity * (0.75 + 0.25 * progress));
      star.radiusSolar = round(giantEntryRadius + (star.massId === "titan" ? 620 : 89) * progress);
      star.composition = star.massId === "titan"
        ? normalizeComposition({ hydrogen: 0.02 - 0.018 * progress, helium: 0.96 - 0.78 * progress, carbonOxygen: 0.018 + 0.48 * progress, silicon: 0.0015 + 0.22 * progress, iron: 0.0005 + 0.098 * progress })
        : normalizeComposition({ hydrogen: 0.02 - 0.015 * progress, helium: 0.96 - 0.65 * progress, carbonOxygen: 0.018 + 0.665 * progress, silicon: 0.0015, iron: 0.0005 });
      break;
    }
    case "terminal":
      if (star.fate === "coreCollapse") {
        star.surfaceTemperatureK = Math.round(4_400 + 1_600 * progress);
        star.luminositySolar = round(45_000 * (1 + 0.5 * progress));
        star.radiusSolar = round(720 - 180 * progress);
        star.composition = normalizeComposition({ hydrogen: 0, helium: 0.1 - 0.08 * progress, carbonOxygen: 0.45 - 0.15 * progress, silicon: 0.3 - 0.05 * progress, iron: 0.15 + 0.28 * progress });
      } else {
        star.surfaceTemperatureK = Math.round(3_700 + 76_300 * progress);
        star.luminositySolar = round(900 * (1 - 0.98 * progress));
        star.radiusSolar = round(110 * (1 - 0.985 * progress));
        star.composition = normalizeComposition({ hydrogen: 0.005, helium: 0.31 - 0.27 * progress, carbonOxygen: 0.683 + 0.27 * progress, silicon: 0.0015, iron: 0.0005 });
      }
      break;
    case "remnant":
      if (star.fate === "coreCollapse") {
        star.surfaceTemperatureK = 600_000;
        star.luminositySolar = 0.02;
        star.radiusSolar = 0.00002;
        star.composition = normalizeComposition({ hydrogen: 0, helium: 0, carbonOxygen: 0.08, silicon: 0.12, iron: 0.8 });
      } else {
        star.surfaceTemperatureK = 25_000;
        star.luminositySolar = 0.01;
        star.radiusSolar = 0.012;
        star.composition = normalizeComposition({ hydrogen: 0.005, helium: 0.04, carbonOxygen: 0.953, silicon: 0.0015, iron: 0.0005 });
      }
      break;
  }
}

function starlightForProgress(state: StellarState, progress: number): number {
  if (!state.star) return 0;
  const stageRate = ({
    cloud: 0.07,
    protostar: 0.14,
    mainSequence: 0.3,
    giant: 0.55,
    terminal: 0.42,
    remnant: 0,
  } satisfies Record<StellarStage, number>)[state.stage];
  const massRate = ({ ember: 0.8, sol: 1, titan: 1.8 } satisfies Record<StellarMassId, number>)[state.star.massId];
  return progress * stageRate * massRate * starlightMultiplier(state);
}

function contractForGeneration(generation: number): StellarContractId {
  return generation % 2 === 0 ? "elementForge" : "stableBeacon";
}

function ejectedMetalsFor(massId: StellarMassId): number {
  return ({ ember: 1, sol: 2, titan: 8 } satisfies Record<StellarMassId, number>)[massId];
}

function unlockLegacyNodes(state: StellarState): StellarLegacyNode[] {
  const unlocked: StellarLegacyNode[] = [];
  for (const node of STELLAR_LEGACY_NODES) {
    if (state.meta.ejectedMetals >= node.metalsRequired && !state.meta.legacyUnlocks.includes(node.id)) {
      state.meta.legacyUnlocks.push(node.id);
      unlocked.push(node);
    }
  }
  return unlocked;
}

function stageReward(stage: StellarStage): number {
  return ({ cloud: 15, protostar: 26, mainSequence: 52, giant: 82, terminal: 110, remnant: 0 } satisfies Record<StellarStage, number>)[stage];
}

function discoveryForStage(stage: StellarStage, star: NonNullable<StellarState["star"]>): string {
  if (stage === "remnant") return star.fate === "coreCollapse" ? "中子星遗迹" : "白矮星遗迹";
  return ({ cloud: "自引力云核", protostar: "原恒星热源", mainSequence: "稳定氢聚变", giant: star.massId === "titan" ? "红超巨星壳层" : "红巨星壳层", terminal: star.fate === "coreCollapse" ? "铁核心坍缩" : "行星状星云抛射", remnant: "恒星遗迹" })[stage];
}

function stageDisplayName(state: StellarState): string {
  if (state.stage === "giant" && state.star?.massId === "titan") return "红超巨星";
  if (state.stage === "terminal" && state.star?.fate === "coreCollapse") return "核心坍缩";
  if (state.stage === "terminal") return "外层抛射";
  if (state.stage === "remnant" && state.star?.fate === "coreCollapse") return "中子星遗迹";
  if (state.stage === "remnant") return "白矮星遗迹";
  return STELLAR_STAGES[state.stage].name;
}

function addHistory(
  state: StellarState,
  id: () => string,
  title: string,
  detail: string,
  stage: StellarStage,
): void {
  state.history.push({ id: id(), stage, generation: state.meta.generation, title, detail });
  state.history = state.history.slice(-HISTORY_LIMIT);
}

function consumePulseTokens(state: StellarState, count: number, nowMs: number): void {
  const safeNow = Number.isFinite(nowMs) ? Math.max(nowMs, state.integrity.pulseTokenUpdatedAt) : state.integrity.pulseTokenUpdatedAt;
  const elapsedSeconds = Math.max(0, safeNow - state.integrity.pulseTokenUpdatedAt) / 1_000;
  const available = Math.min(MAX_PULSE_TOKENS, state.integrity.pulseTokens + elapsedSeconds * PULSE_TOKENS_PER_SECOND);
  if (count > available + 1e-9) throw new StellarRuleError("PULSE_RATE_LIMITED", "演化脉冲过快，请稍候再试");
  state.integrity.pulseTokens = round(available - count);
  state.integrity.pulseTokenUpdatedAt = safeNow;
}

function requireBootstrap(state: StellarState): void {
  if (!state.tutorial.bootstrapComplete) throw new StellarRuleError("BOOTSTRAP_REQUIRED", "请先完成基础模型导入");
}

function requireActiveStar(state: StellarState, allowRemnant = false): void {
  requireBootstrap(state);
  if (!state.star) throw new StellarRuleError("STAR_REQUIRED", "请先选择恒星质量");
  if (!allowRemnant && state.stage === "remnant") throw new StellarRuleError("STAR_COMPLETE", "当前恒星已经完成演化");
}

function level(state: StellarState, id: string): number {
  return state.upgrades[id] ?? 0;
}

function permanentLevel(state: StellarState, id: string): number {
  return state.permanentUpgrades[id] ?? 0;
}

function normalizeComposition(composition: CoreComposition): CoreComposition {
  const values = Object.values(composition).map((value) => Math.max(0, value));
  const sum = values.reduce((total, value) => total + value, 0) || 1;
  return {
    hydrogen: round(values[0]! / sum),
    helium: round(values[1]! / sum),
    carbonOxygen: round(values[2]! / sum),
    silicon: round(values[3]! / sum),
    iron: round(values[4]! / sum),
  };
}

function assertFiniteState(state: StellarState): void {
  const numbers: number[] = [
    state.meta.generation,
    state.meta.completedStars,
    state.meta.totalPulses,
    state.meta.totalSimulatedSeconds,
    state.meta.stardust,
    state.meta.completedContracts,
    state.meta.ejectedMetals,
    state.stageProgress,
    state.resources.insight,
    state.resources.starlight,
    state.integrity.pulseTokens,
    state.integrity.pulseTokenUpdatedAt,
  ];
  if (state.star) {
    numbers.push(
      state.star.massSolar,
      state.star.surfaceTemperatureK,
      state.star.luminositySolar,
      state.star.radiusSolar,
      state.star.lifetimeYears,
      state.star.ageFraction,
      state.star.remnantMassSolar,
      state.star.ejectaMassSolar,
      ...Object.values(state.star.composition),
    );
    const massLedger = state.star.remnantMassSolar + state.star.ejectaMassSolar;
    if (Math.abs(massLedger - state.star.massSolar) > 1e-5) throw new Error("stellar mass ledger is not conserved");
  }
  if (numbers.some((value) => !Number.isFinite(value) || value < 0)) throw new Error("stellar state contains invalid number");
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function round(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}
